// Cross-backend client for the unified-portal "Pitstop Notifications" tab.
//
// admin-ui's global axios defaults pin every request to admin-corev2
// (X-Request-From: admin + dex + the admin Bearer token), so we CANNOT use axios
// here. Native fetch with explicit pitstop headers is the same approach the
// dual-backend logout uses (redux/actions/auth.js postLogoutKeepalive).
//
// 401 handling is deliberately local: a stale PITSTOP token must NOT trigger the
// shared handleError's sessionStorage.clear()/redirect (that would log the user
// out of admin too). We surface it as { sessionExpired: true } so the tab can
// show its own message. This client is only used in shell-hosted mode.

const PITSTOP_BASE = '/api/v1';

function pitstopHeaders() {
	const token = sessionStorage.getItem('pitstopCoreToken');
	const hostname = sessionStorage.getItem('pitstopHostname');
	return {
		'Content-Type': 'application/json',
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		'X-Request-From': 'pitstop',
		// pitstop-core's authMiddleware only validates the JWT (and sets the real
		// req.user) when Channel === 'UI'; without it the call falls into the
		// "Source System" branch → no user id → 400. pitstop-ui sets this globally.
		Channel: 'UI',
		// Bare hostname, no scheme — with https:// the tenant lookup 400s.
		...(hostname ? { 'X-Pitstop-Hostname': hostname } : {}),
	};
}

async function request(method, path, body) {
	try {
		const res = await fetch(`${PITSTOP_BASE}${path}`, {
			method,
			headers: pitstopHeaders(),
			...(body !== undefined ? { body: JSON.stringify(body) } : {}),
		});

		if (res.status === 401) {
			return [{ status: 401, sessionExpired: true }, undefined];
		}

		if (!res.ok) {
			let errBody;
			try { errBody = await res.json(); } catch { errBody = null; }
			return [errBody?.error || errBody || `Request failed (${res.status})`, undefined];
		}

		let json;
		try { json = await res.json(); } catch { json = undefined; }
		// Mirror services/api/utils/response.js: unwrap a nested { data } envelope.
		const data = json && Object.hasOwn(json, 'data') ? json.data : json;
		return [undefined, data];
	} catch (err) {
		return [err, undefined];
	}
}

// Same signature/return shape as pitstop-ui's settingApi so the ported component
// body is unchanged: returns [error, data].
export const pitstopSettingsApi = {
	getNotifications: async (options = {}) => {
		const { setLoading, addToastMessage } = options;
		if (setLoading) setLoading(true);
		const [error, data] = await request('GET', '/settings/user-notifications');
		if (error && !error.sessionExpired && addToastMessage) {
			addToastMessage('Failed to get notification settings', 'error');
		}
		if (setLoading) setLoading(false);
		return [error, data];
	},

	updateNotifications: async (payload, options = {}) => {
		const { setLoading, addToastMessage } = options;
		if (setLoading) setLoading(true);
		const [error, data] = await request('PUT', '/settings/user-notifications', payload);
		if (error && !error.sessionExpired && addToastMessage) {
			addToastMessage('Failed to update notification settings', 'error');
		}
		if (setLoading) setLoading(false);
		return [error, data];
	},

	// Pitstop config (produces/consumes) → the data-element universe for the
	// EMAIL_PREFERENCE modify feature. The component prefers pitstop-ui's own
	// sessionStorage `config` cache and only calls this as the fallback.
	getConfig: async () => {
		return request('GET', '/config');
	},

	// Proactively refresh the pitstop token when the tab opens. The pitstop token
	// ages independently of the admin token (each surface's renew timer only runs
	// while that SPA is mounted), so without this a long admin-only session leaves
	// a near-stale pitstop token → 401 on first use. Best-effort: on success we
	// write the fresh token back; a 401 here means it's already expired server-side.
	renewToken: async () => {
		const [error, data] = await request('POST', '/renew-token');
		const token = data?.token;
		if (!error && token) {
			sessionStorage.setItem('pitstopCoreToken', token);
		}
		return [error, data];
	},
};
